<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { $bus } from '@/common/events'
import { useVirtualKeyboard } from '@/composables/keyboard'
import Spinner from '@/components/Flowbite/Spinner.vue'

const { vkeyboard, activeTarget } = useVirtualKeyboard();

const props = defineProps({
  id: {
    type: String,
    default: ""
  },
  name: {
    type: String,
    default: ""
  },
  loading: {
    default: false
  },
  enabled: {
    type: Boolean,
    default: true
  },
  readonly: {
    type: Boolean,
    default: false
  },
  resizable: {
    type: Boolean,
    default: true
  },
  autoSearch: { //Automatically search/filter while typing?
    type: Boolean,
    default: true //true..search while typing, false..search when on button click
  },
  placeholder: {
    type: String,
    default: ""
  },
  accesskey: {
    type: String,
    default: ""
  },
  suggestions: {
    type: Array,
    default: [ /* { name } */ ]
  },
  modelValue: {
    type: String,
    default: ""
  },
});

const emit = defineEmits([ 'update:modelValue', 'text-changed', 'search-request', 'suggestion-selected', 'enter' ]);

const value = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
    emit('text-changed', value);
  }
});

const isReadOnly = computed({
  get() {
    return props.readonly || props.loading;
  }
});

const textbox = ref(null);

const isFocusForKeyboard = computed(() => activeTarget.value === textbox.value);

function handleKeyboardHidden() {
  isAutocompleteVisible.value = false;
}

watch(isFocusForKeyboard, (focused) => {
  if (focused) {
    vkeyboard.on("enter", onEnterKey);
  }
  else {
    vkeyboard.removeListener("enter", onEnterKey);
  }
});

onMounted(() => {
  $bus.on("keyboard hidden", handleKeyboardHidden);
});

onUnmounted(() => {
  $bus.off("keyboard hidden", handleKeyboardHidden);
  vkeyboard.removeListener("enter", onEnterKey);
});

function highlightCaseSensitive(label, input) {
  if (!input) {
    return label;
  }

  let keywords = label.split(input).reduce((prev, current, i) => {
    if (!i) {
      return [ current ];
    }
    return prev.concat("<b key=" + input + current + ">" + input + "</b>", current);
  }, []);

  return "<span>" + keywords.join("") + "</span>";
}

function escapeRegExp(text) {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

function highlight(label, input) {
  if (!input) {
    return label;
  }

  const regexp = new RegExp(`${escapeRegExp(input)}`, 'i');
  const formatedLabel = label.replace(regexp, '<b>$&</b>');
  return `<span>${formatedLabel}</span>`;
}

const isAutocompleteVisible = ref(false);

function focus(e) {
  showAutocomplete(e);
  showVirtualKeyboard(e);
  //isFocusForKeyboard.value = true;
}

function unfocus(e) {
  //if (!isFocusForKeyboard.value) {
    hideAutocomplete(e);
    hideVirtualKeyboard(e);
  //}
}

defineExpose({ focus, unfocus });

function onFocus(e) {
  if (props.enabled && !props.readonly) {
    focus(e);
  }
}

function onUnfocus(e) {
  if (props.enabled && !props.readonly) {
    unfocus(e);
  }
}

function showAutocomplete(e) {
  isAutocompleteVisible.value = true;
}

function hideAutocomplete() {
  isAutocompleteVisible.value = false;
}

function showVirtualKeyboard(e) {
  if (props.enabled && !props.readonly) {
    vkeyboard.show(e.target);
  }
}

function hideVirtualKeyboard(e) {
  if (props.enabled && !props.readonly) {
    vkeyboard.hide(e.target, { delay: 120 });
  }
}

function selectSuggestion(suggestion) {
  emit('suggestion-selected', suggestion);
}

function onEnterKey() {
  emit("enter");
  emit('search-request', value.value);
}

const hasSearchText = computed(() => {
  return isFocusForKeyboard.value && value.value && value.value.trim().length > 0;
});

function iconButtonClick(e) {
  //In case of auto search this icon is a clear search command
  let isClearCommand = props.autoSearch && hasSearchText.value;
  if (isClearCommand) {
    value.value = "";
    unfocus(e);
  }

  emit('search-request', value.value);
}
</script>

<template>
  <div class="flex items-center" style="position: relative;">
    <!-- Text input -->
    <input type="text" ref="textbox" v-model="value" :accesskey="accesskey" class="text-search"
      :class="{ 'resizable': props.resizable, 'focus-for-keyboard': isFocusForKeyboard }" :placeholder="props.placeholder" :readonly="isReadOnly"
      @change="emit('text-changed', value)" @keypress.enter="emit('search-request', value)" @focus="onFocus" @blur="onUnfocus" />

    <!-- Icon button -->
    <div class="w-4 h-4" style="position: absolute; top: 9px; right: 12px;"
      :class="{ 'text-gray-400 dark:text-gray-600': !isFocusForKeyboard, 'text-blue-700 dark:text-blue-300': isFocusForKeyboard }"
      v-if="!props.loading" @click="iconButtonClick">
        <i class="fa fa-xmark" v-if="hasSearchText && props.autoSearch"></i>
        <i class="fa fa-magnifying-glass" v-else></i>
    </div>

    <!-- Loading animation -->
    <Spinner size="5" style="position: absolute; right: 12px;" v-if="props.loading" />

    <!-- Autocomplete suggestions drop down menu -->
    <div class="z-10 mt-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg dark:bg-gray-700" style="position: absolute; left: 0; top: 3px;" v-if="isAutocompleteVisible && props.suggestions && props.suggestions.length">
      <ul class="py-2 text-sm text-gray-700 dark:text-gray-200" aria-labelledby="dropdownDelayButton">
        <li v-for="suggestion in props.suggestions">
          <a href="#" class="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white" @click.prevent="selectSuggestion(suggestion)">
            <i class="text-gray-400 mr-3 fa" :class="suggestion.icon" v-if="suggestion.icon"></i>
            <span v-html="highlight(suggestion.name, value)"></span>
          </a>
        </li>
      </ul>
    </div>

  </div>
</template>

<style scoped>
input[type="text"].text-search {
  @apply bg-white border border-gray-300 text-gray-900 text-sm rounded-lg
    focus:ring-blue-500 focus:border-blue-500
    block w-full pl-4 pr-10 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500;
}

input[type="text"].text-search.resizable {
  width: 200px;
  transition: all .2s;
}

input[type="text"].text-search.resizable:focus,
input[type="text"].text-search.resizable.focus-for-keyboard {
  width: 450px;
}

input[type="text"].text-search:focus,
input[type="text"].text-search.focus-for-keyboard {
  @apply ring-blue-500 border-blue-500;

  outline: 2px solid transparent;
  outline-offset: 2px;
  --tw-ring-inset: var(--tw-empty, );
  --tw-ring-offset-width: 0px;
  --tw-ring-offset-color: #fff;
  --tw-ring-color: #1C64F2;
  --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
  --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color);
  box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow);
  border-color: #1C64F2;
}
</style>
